/**
 * Service for interacting with Lens Anchor APIs
 */

import {
  LensAnchorDescriptor,
  AnchorSelection,
  LensletContent
} from '../types/lensAnchorTypes';
import { getAPIHeaders } from '../utils/apiHeaders';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

/**
 * Call the Selection Engine to pick pedagogically valuable anchors
 */
export async function suggestLensAnchors(params: {
  known: string;
  target: string;
  lensAnchors: LensAnchorDescriptor[];
  max?: number;
  codeHash?: string;
  grammarVersion?: string;
  selectorSchemaVersion?: string;
}): Promise<{
  picks: AnchorSelection[];
  dropReasons: Record<string, string>;
  metadata?: {
    cacheKey: string;
    responseTimeMs: number;
    inputCount: number;
    filteredCount: number;
  };
}> {
  try {
    const response = await fetch(`${API_BASE_URL}/api/lens-anchors/suggest`, {
      method: 'POST',
      headers: getAPIHeaders(),
      body: JSON.stringify({
        known: params.known,
        target: params.target,
        lensAnchors: params.lensAnchors,
        max: params.max || 8,
        codeHash: params.codeHash || generateCodeHash(params.lensAnchors),
        grammarVersion: params.grammarVersion || 'v1',
        selectorSchemaVersion: params.selectorSchemaVersion || 'v1'
      })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.details || 'Selection engine error');
    }

    const result = await response.json();
    
    // Normalize diffType to always be arrays (safety check)
    if (result.picks) {
      result.picks = result.picks.map((pick: any) => ({
        ...pick,
        diffType: Array.isArray(pick.diffType) ? pick.diffType : [pick.diffType]
      }));
    }

    return result;
  } catch (error) {
    console.error('Error calling selection engine:', error);
    throw error;
  }
}

/**
 * Generate lenslet content for selected anchors using SSE
 */
export async function generateLensletContent(params: {
  picks: AnchorSelection[];
  anchors: LensAnchorDescriptor[];
  known: string;
  target: string;
  lensId?: string;
  customInstructions?: string;
  provider?: {
    provider: string;
    model: string;
    status?: string;
  };
  onLenslet?: (lenslet: LensletContent) => void;
  onComplete?: () => void;
  onError?: (error: Error) => void;
  onPromptCapture?: (prompt: string, metadata: any) => void;
}): Promise<void> {
  console.log('🚀 Starting lenslet generation with params:', {
    picksCount: params.picks.length,
    anchorsCount: params.anchors.length,
    known: params.known,
    target: params.target,
    picks: params.picks.map(p => ({ tempId: p.tempId, text: p.text }))
  });
  
  let chunkCount = 0;
  let lensletCount = 0;
  let errorCount = 0;
  
  try {
    console.log('📡 Making fetch request to generation endpoint...');
    const response = await fetch(`${API_BASE_URL}/api/lens-anchors/generate`, {
      method: 'POST',
      headers: getAPIHeaders(),
      body: JSON.stringify({
        picks: params.picks,
        anchors: params.anchors,
        known: params.known,
        target: params.target,
        lensId: params.lensId,
        customInstructions: params.customInstructions,
        provider: params.provider
      })
    });

    console.log('📡 Fetch response received:', {
      ok: response.ok,
      status: response.status,
      statusText: response.statusText,
      headers: Object.fromEntries(response.headers.entries())
    });

    if (!response.ok) {
      console.error('❌ Generation engine HTTP error:', {
        status: response.status,
        statusText: response.statusText
      });
      throw new Error(`Generation engine error: ${response.status} ${response.statusText}`);
    }

    // Handle Server-Sent Events
    console.log('🔄 Setting up SSE stream reader...');
    const reader = response.body?.getReader();
    const decoder = new TextDecoder();

    if (!reader) {
      console.error('❌ No response body available');
      throw new Error('No response body');
    }

    console.log('✅ SSE reader setup complete, starting stream processing...');
    
    while (true) {
      console.log(`🔄 Reading SSE chunk ${++chunkCount}...`);
      const { done, value } = await reader.read();
      
      console.log('📦 SSE chunk read result:', {
        done,
        valueLength: value?.length || 0,
        chunkNumber: chunkCount
      });
      
      if (done) {
        console.log('✅ SSE stream completed');
        break;
      }

      const chunk = decoder.decode(value);
      console.log(`📝 Decoded chunk ${chunkCount}:`, {
        length: chunk.length,
        preview: chunk.substring(0, 200) + (chunk.length > 200 ? '...' : ''),
        lineCount: chunk.split('\n').length
      });
      
      const lines = chunk.split('\n');

      console.log(`🔍 Processing ${lines.length} lines from chunk ${chunkCount}...`);
      
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        console.log(`📄 Processing line ${i + 1}/${lines.length}:`, {
          startsWithData: line.startsWith('data: '),
          lineLength: line.length,
          linePreview: line.substring(0, 100)
        });
        
        if (line.startsWith('data: ')) {
          const data = line.slice(6);
          
          console.log('🔍 SSE Data Line Found:', {
            data: data.substring(0, 200) + (data.length > 200 ? '...' : ''),
            fullLength: data.length,
            isDone: data === '[DONE]',
            isError: data.includes('error'),
            isMetadata: data.includes('metadata'),
            isTempId: data.includes('tempId')
          });
          
          if (data === '[DONE]') {
            console.log('🏁 Received completion signal [DONE]');
            params.onComplete?.();
            return;
          }

          try {
            console.log('🔄 Attempting to parse JSON data...');
            const parsed = JSON.parse(data);
            console.log('✅ JSON parsed successfully:', {
              type: parsed.type,
              hasPrompt: !!parsed.prompt,
              hasTempId: !!parsed.tempId,
              hasLenslet: !!parsed.lenslet,
              hasError: !!parsed.error,
              keys: Object.keys(parsed)
            });
            
            // Handle metadata event for AI observability
            if (parsed.type === 'metadata') {
              console.log('📋 Processing metadata event:', {
                hasPrompt: !!parsed.prompt,
                promptLength: parsed.prompt?.length,
                totalPicks: parsed.totalPicks,
                timestamp: parsed.timestamp,
                requestId: parsed.requestId,
                hasOnPromptCapture: !!params.onPromptCapture
              });
              
              params.onPromptCapture?.(parsed.prompt, {
                totalPicks: parsed.totalPicks,
                timestamp: parsed.timestamp,
                requestId: parsed.requestId
              });
              console.log('✅ Metadata event processed successfully');
            } else if (parsed.tempId && parsed.lenslet) {
              // Handle regular lenslet content
              lensletCount++;
              console.log(`🎯 Processing lenslet ${lensletCount} for tempId: ${parsed.tempId}`);
              const lenslet = parsed as LensletContent;
              params.onLenslet?.(lenslet);
              console.log(`✅ Lenslet ${lensletCount} processed successfully`);
            } else if (parsed.error) {
              // Handle error events
              errorCount++;
              console.error(`❌ Processing error event ${errorCount}:`, parsed.error);
              params.onError?.(new Error(parsed.error));
            } else {
              console.warn('⚠️ Unknown SSE event type received:', {
                keys: Object.keys(parsed),
                preview: JSON.stringify(parsed).substring(0, 200)
              });
            }
          } catch (e) {
            // Skip invalid JSON
            console.error('❌ Failed to parse SSE JSON data:', {
              error: e,
              dataPreview: data.substring(0, 100),
              dataLength: data.length,
              containsError: data.includes('error'),
              parseAttemptFailed: true
            });
            
            if (data.includes('error')) {
              try {
                console.log('🔄 Attempting to parse as error message...');
                const error = JSON.parse(data);
                console.error('📋 Parsed error message:', error);
                params.onError?.(new Error(error.error || 'Unknown error'));
              } catch (parseError) {
                console.error('❌ Failed to parse error message:', parseError);
                params.onError?.(new Error('Failed to parse error message'));
              }
            }
          }
        }
      }
    }

    console.log('🏁 SSE processing completed normally', {
      totalChunks: chunkCount,
      totalLenslets: lensletCount,
      totalErrors: errorCount
    });
    params.onComplete?.();
  } catch (error) {
    console.error('💥 Fatal error in generateLensletContent:', {
      error: error,
      message: error instanceof Error ? error.message : 'Unknown error',
      stack: error instanceof Error ? error.stack : undefined,
      totalChunks: chunkCount,
      totalLenslets: lensletCount,
      totalErrors: errorCount
    });
    params.onError?.(error as Error);
    throw error;
  }
}

/**
 * Generate a simple hash for code/anchors
 */
function generateCodeHash(anchors: LensAnchorDescriptor[]): string {
  const text = anchors.map(a => a.text).join('');
  let hash = 0;
  for (let i = 0; i < text.length; i++) {
    const char = text.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash).toString(36);
}

/**
 * Combined flow: Enumerate → Select → Generate
 */
export async function processLensAnchors(params: {
  anchors: LensAnchorDescriptor[];
  known: string;
  target: string;
  max?: number;
  onProgress?: (stage: 'selecting' | 'generating', data?: any) => void;
}): Promise<{
  selections: AnchorSelection[];
  lenslets: Map<string, LensletContent>;
}> {
  // Step 1: Call selection engine
  params.onProgress?.('selecting');
  
  const selectionResult = await suggestLensAnchors({
    known: params.known,
    target: params.target,
    lensAnchors: params.anchors,
    max: params.max
  });

  // Step 2: Generate content for selected anchors
  params.onProgress?.('generating');
  
  const lenslets = new Map<string, LensletContent>();
  
  await generateLensletContent({
    picks: selectionResult.picks,
    anchors: params.anchors,
    known: params.known,
    target: params.target,
    onLenslet: (lenslet) => {
      lenslets.set(lenslet.tempId, lenslet);
      params.onProgress?.('generating', { 
        completed: lenslets.size, 
        total: selectionResult.picks.length 
      });
    }
  });

  return {
    selections: selectionResult.picks,
    lenslets
  };
}

/**
 * Simulate selection for testing/fallback
 */
export function simulateSelection(
  anchors: LensAnchorDescriptor[],
  known: string,
  target: string
): AnchorSelection[] {
  // Simple heuristic-based selection
  return anchors
    .filter(a => a.confidence > 0.5)
    .slice(0, 8)
    .map((anchor, index) => {
      const diffTypes: any[] = [];
      let novelty = anchor.confidence;
      let why = '';

      // Apply language-specific heuristics
      if (anchor.features.some(f => f.includes('ownership') || f.includes('borrow'))) {
        diffTypes.push('paradigm');
        novelty = Math.min(novelty * 1.5, 1.0);
        why = 'Ownership system - key paradigm shift';
      } else if (anchor.features.some(f => f.includes('memory'))) {
        diffTypes.push('semantic');
        novelty = Math.min(novelty * 1.3, 1.0);
        why = 'Memory management difference';
      } else if (anchor.text.includes('::')) {
        diffTypes.push('syntax');
        why = 'Namespace/module syntax';
      } else {
        diffTypes.push('syntax');
        why = 'Language-specific syntax';
      }

      return {
        tempId: anchor.tempId,
        novelty,
        diffType: diffTypes,
        why,
        granularity: anchor.granularity,
        group: anchor.features[0]?.split(':')[0],
        priority: index + 1
      };
    });
}